import type { RowData } from "@tanstack/react-table";
import {
    selectAllRange,
    selectRangeColumns,
    selectRangeRows,
} from "../features/range/rangeActions";
import {
    toggleAllInScope,
    toggleRowSelection,
} from "../features/selection/rowSelection";
import {
    canSelectByKeyboard,
    type Cursor,
    extend,
    focusAt,
    type GridActionInput,
    move,
    pageSize,
} from "./gridMoves";
import { enterCell, escapeGrid, focusSearch, openRow } from "./leaveActions";
import { runListAction } from "./listActions";
import type { GridKeyAction } from "./keyMap";
import { getFocusedAddress, getNavigableColumnIds } from "./navigation";
import { endSelectionRun } from "./selectionRun";

const runAction = <TData extends RowData>(
    cursor: Cursor<TData>,
    action: Exclude<GridKeyAction, { type: "focusSearch" }>,
): boolean => {
    const { instance } = cursor.input;
    const { hasCellNavigation, table } = instance;
    const row = cursor.rows[cursor.rowIndex];
    const lastRowIndex = cursor.rows.length - 1;
    const firstColumnId = cursor.columnIds[0];
    const lastColumnId = cursor.columnIds[cursor.columnIds.length - 1];
    switch (action.type) {
        case "move":
            return move(cursor, action.direction);
        case "extend":
            return extend(cursor, action.direction);
        case "rowStart":
            return hasCellNavigation
                ? focusAt(cursor, {
                      rowIndex: cursor.rowIndex,
                      columnId: firstColumnId,
                  })
                : focusAt(cursor, { rowIndex: 0 });
        case "rowEnd":
            return hasCellNavigation
                ? focusAt(cursor, {
                      rowIndex: cursor.rowIndex,
                      columnId: lastColumnId,
                  })
                : focusAt(cursor, { rowIndex: lastRowIndex });
        case "gridStart":
            return focusAt(cursor, { rowIndex: 0, columnId: firstColumnId });
        case "gridEnd":
            return focusAt(cursor, {
                rowIndex: lastRowIndex,
                columnId: lastColumnId,
            });
        case "pageUp":
            return focusAt(cursor, {
                rowIndex: cursor.rowIndex - pageSize(cursor),
            });
        case "pageDown":
            return focusAt(cursor, {
                rowIndex: cursor.rowIndex + pageSize(cursor),
            });
        case "open":
            return (
                (hasCellNavigation && enterCell(cursor.input.target)) ||
                openRow({
                    instance,
                    row,
                    columnId: hasCellNavigation
                        ? cursor.current?.columnId
                        : undefined,
                })
            );
        case "toggleRowSelection":
            if (!canSelectByKeyboard(cursor, row)) {
                return false;
            }
            toggleRowSelection({ row, isRangeEvent: false });
            return true;
        case "selectAll":
            if (hasCellNavigation && instance.hasRangeSelection) {
                return selectAllRange(table);
            }
            if (
                !hasCellNavigation &&
                Boolean(table.options.enableRowSelection) &&
                !instance.optionsRef.current.rowSelection
                    ?.isKeyboardSelectionDisabled
            ) {
                toggleAllInScope({
                    table,
                    scope: instance.optionsRef.current.rowSelection
                        ?.selectAllScope,
                    isSelected: true,
                });
                return true;
            }
            return false;
        case "selectRangeRows":
            return instance.hasRangeSelection && selectRangeRows(table);
        case "selectRangeColumns":
            return instance.hasRangeSelection && selectRangeColumns(table);
        case "escape":
            return escapeGrid(cursor.input);
    }
};

/**
 * Runs a grid key action against the focused cell. Returns whether the key
 * was handled, so the caller can prevent the browser default.
 */
export const runGridAction = <TData extends RowData>(
    input: GridActionInput<TData>,
): boolean => {
    const { instance, controller, action } = input;
    const isRunStep =
        action.type === "extend" &&
        (action.direction === "up" || action.direction === "down");
    if (!isRunStep) {
        endSelectionRun(controller);
    }
    const rows = instance.table.getRowModel().rows;
    const current = getFocusedAddress(instance.table.atoms.cellSelection.get());
    const rowIndex = current
        ? (controller.rowIndexById.get(current.rowId) ?? -1)
        : -1;
    const listResult = instance.hasListEditing
        ? runListAction({ ...input, focused: { current, rows, rowIndex } })
        : null;
    if (listResult !== null) {
        return listResult;
    }
    if (action.type === "focusSearch") {
        return focusSearch(controller);
    }
    if (rows.length === 0) {
        return false;
    }
    const columnIds = getNavigableColumnIds(instance.table);
    const cursor: Cursor<TData> = {
        input,
        rows,
        columnIds,
        current,
        rowIndex,
        columnIndex: current ? columnIds.indexOf(current.columnId) : -1,
    };
    if (rowIndex < 0) {
        if (action.type === "escape") {
            return escapeGrid(input);
        }
        return focusAt(
            { ...cursor, current: null },
            { rowIndex: 0, columnId: columnIds[0] },
        );
    }
    return runAction(cursor, action);
};
